import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { apiPost, apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminFormDialog, AdminField } from "./AdminFormDialog";
import { Input } from "../ui/input";

export type PartnerFormState = {
  name: string;
  url: string;
  description: string;
  logo: string;
  sortOrder: string;
  isActive: boolean;
};

export const EMPTY_PARTNER: PartnerFormState = {
  name: "", url: "", description: "", logo: "", sortOrder: "", isActive: true,
};

type Props = {
  open: boolean;
  editing: any | null;
  onClose: () => void;
  onSaved: () => void;
};

const FORM_ID = "partner-form";

export const AdminPartnerModal: React.FC<Props> = ({ open, editing, onClose, onSaved }) => {
  const { t } = useLanguage();

  const schema = Yup.object({
    name: Yup.string().trim().required(t.common.required),
    url: Yup.string().url("URL invalide").nullable().transform((v, o) => (o === "" ? null : v)),
    description: Yup.string(),
    logo: Yup.string(),
    sortOrder: Yup.number().typeError("Nombre invalide").min(0).nullable().transform((v, o) => (o === "" ? null : v)),
    isActive: Yup.boolean(),
  });

  const initial: PartnerFormState = editing ? {
    name: editing.name || "",
    url: editing.url || "",
    description: editing.description || "",
    logo: editing.logo || "",
    sortOrder: editing.sortOrder != null ? String(editing.sortOrder) : "",
    isActive: editing.isActive !== false,
  } : EMPTY_PARTNER;

  return (
    <Formik
      initialValues={initial}
      validationSchema={schema}
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        const payload = {
          name: values.name.trim(),
          url: values.url.trim() || null,
          description: values.description.trim() || null,
          logo: values.logo.trim() || null,
          sortOrder: values.sortOrder !== "" ? Number(values.sortOrder) : undefined,
          isActive: values.isActive,
        };
        try {
          if (editing) await apiPut(`/partners/${editing.id}`, payload);
          else await apiPost("/partners", payload);
          toast.success(t.common.saveSuccess);
          onSaved();
          onClose();
        } catch (e: any) {
          toast.error(e?.response?.data?.reason || e?.messageText || t.common.error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue }) => (
        <AdminFormDialog
          open={open}
          onClose={onClose}
          title={editing ? "Modifier le partenaire" : "Nouveau partenaire"}
          subtitle="Informations du partenaire"
          testId="partner-modal"
          formId={FORM_ID}
          submitLabel={editing ? t.admin.save : t.admin.create}
          isSubmitting={isSubmitting}
        >
          <Form id={FORM_ID} className="grid gap-4 pb-4">
            <AdminField
              label="Nom"
              required
              error={touched.name ? errors.name : undefined}
            >
              <Field
                as={Input}
                name="name"
                data-testid="partner-name"
                placeholder="Ex: Université Protestante au Congo"
              />
            </AdminField>
            <AdminField
              label="Site web"
              error={touched.url ? errors.url : undefined}
            >
              <Field
                as={Input}
                name="url"
                data-testid="partner-url"
                placeholder="https://upc.ac.cd"
              />
            </AdminField>
            <AdminField label="Description">
              <Field
                as={Input}
                name="description"
                data-testid="partner-description"
                placeholder="Brève description du partenaire"
              />
            </AdminField>
            <AdminField label="URL du logo">
              <Field
                as={Input}
                name="logo"
                data-testid="partner-logo"
                placeholder="https://example.com/logo.png"
              />
            </AdminField>
            <div className="grid grid-cols-2 gap-4">
              <AdminField label="Ordre d'affichage">
                <Field as={Input} id="partner-order" name="sortOrder" type="number" min={0} />
              </AdminField>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={values.isActive}
                    onChange={(e) => setFieldValue("isActive", e.target.checked)}
                  />
                  {values.isActive ? "Actif" : "Inactif"}
                </label>
              </div>
            </div>
          </Form>
        </AdminFormDialog>
      )}
    </Formik>
  );
};
